import type { Invoice, Donation } from '@/types/firebase';
import { getApiBaseUrl } from '@/lib/environment';
import { v4 as uuidv4 } from 'uuid';

interface SendInvoiceEmailParams {
  invoice: Invoice;
  template: string;
  subject: string;
  isCustomContent?: boolean;
  isDonation?: boolean;
}

interface SendDonationThankYouEmailParams {
  donation: Donation;
  template: string;
  subject: string;
}

export async function sendInvoiceEmail({ invoice, template, subject, isCustomContent = false, isDonation = false }: SendInvoiceEmailParams) {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const apiUrl = `${apiBaseUrl}/api/email`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        invoice,
        template,
        subject,
        isCustomContent,
        isDonation,
      }),
    });

    const responseText = await response.text();

    let responseData;
    try {
      responseData = JSON.parse(responseText);
    } catch (_error) {
      console.error('Failed to parse email API response as JSON:', responseText);
      throw new Error('Invalid response from server');
    }

    if (!response.ok) {
      throw new Error(responseData.error || `Failed to send email: ${response.status} ${response.statusText}`);
    }

    return responseData.updatedInvoice || invoice;
  } catch (error) {
    console.error('Error sending invoice email:', error);
    throw error;
  }
}

export async function sendThankYouEmail({ invoice, template, subject }: SendInvoiceEmailParams) {
  return sendInvoiceEmail({
    invoice,
    template,
    subject,
    isCustomContent: false,
  });
}

/**
 * Send a thank you email after a successful donation
 */
export async function sendDonationThankYouEmail({ donation, template, subject }: SendDonationThankYouEmailParams) {
  try {
    if (!donation.email) {
      console.warn('No email address available for donation thank you email');
      return null;
    }

    const formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(donation.amount);

    const formattedDate = donation.createdAt.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    const virtualInvoice: Invoice = {
      id: donation.id,
      invoiceNumber: `DON-${donation.id}`,
      customerName: `${donation.firstName} ${donation.lastName}`,
      customerEmail: donation.email || '',
      total: donation.amount,
      date: donation.createdAt.toISOString().split('T')[0],
      status: 'paid' as const,
      companyId: donation.companyId,
      customerId: `donation_${donation.id}`,
      customerAddress: `${donation.address || ''}, ${donation.city || ''}, ${donation.state || ''} ${donation.zip || ''}`.trim(),
      dueDate: donation.createdAt.toISOString().split('T')[0],
      items: [
        {
          id: uuidv4(),
          categoryName: 'Donations',
          productName: 'Donation',
          description: 'One-time donation',
          quantity: 1,
          unitPrice: donation.amount,
          total: donation.amount,
          isTaxable: false,
        },
      ],
      subtotal: donation.amount,
      taxRate: 0,
      taxAmount: 0,
      platformFee: donation.platformFeeAmount || 0,
      paymentMethods: ['credit_card'],
      createdAt: donation.createdAt,
      updatedAt: donation.createdAt,
      metadata: {
        isDonation: true,
        donationAmount: formattedAmount,
        paymentDate: formattedDate,
        platformFeeEnabled: donation.platformFeeEnabled || false,
        platformFeeAmount: donation.platformFeeAmount || 0,
        platformFeePercentage: donation.platformFeePercentage || 0,
        dynamicFields: donation.dynamicFields || {},
      },
    };

    return sendInvoiceEmail({
      invoice: virtualInvoice,
      template,
      subject,
      isCustomContent: false,
      isDonation: true,
    });
  } catch (error) {
    console.error('Error sending donation thank you email:', error);
    throw error;
  }
}
